import { type ReactNode, useCallback, useMemo } from 'react';
import {
  type ChoicesProps,
  FieldTitle,
  type InputProps,
  useChoices,
  useChoicesContext,
  useInput,
} from 'ra-core';
import { FormError, FormField, FormLabel } from '../../components/admin/form';
import { InputHelperText } from '../../components/admin/input-helper-text';
import { Checkbox } from '../../components/ui/checkbox';
import { cn } from '../../lib/utils';

export type CheckboxGroupInputProps = Omit<InputProps, 'source'> &
  Partial<Pick<InputProps, 'source'>> &
  ChoicesProps & {
    className?: string;
    helperText?: ReactNode;
  };

/**
 * A list of checkboxes for an array value, e.g. inside a `ReferenceArrayInput` (the kit has no `CheckboxGroupInput`).
 * `optionText` can be a field name or a render function of the choice.
 */
export const CheckboxGroupInput = (props: CheckboxGroupInputProps) => {
  const {
    className,
    helperText,
    label,
    optionText,
    optionValue = 'id',
    translateChoice,
    readOnly,
    disabled,
  } = props;
  const {
    allChoices = [],
    source,
    resource,
    isFromReference,
  } = useChoicesContext(props);
  const { id, field, isRequired } = useInput({
    ...props,
    source,
    defaultValue: props.defaultValue ?? [],
  });
  const { getChoiceText, getChoiceValue } = useChoices({
    optionText,
    optionValue,
    translateChoice: translateChoice ?? !isFromReference,
  });

  const isReadOnly = !!(readOnly || disabled);
  const values = useMemo<unknown[]>(
    () => (Array.isArray(field.value) ? field.value : []),
    [field.value]
  );

  const handleToggle = useCallback(
    (value: unknown, checked: boolean) => {
      const next = checked
        ? [...values, value]
        : values.filter((existing) => !isSameValue(existing, value));
      field.onChange(next);
      field.onBlur();
    },
    [field, values]
  );

  const labelId = `${id}-label`;

  return (
    <FormField className={cn('w-full', className)} id={id} name={field.name}>
      {label !== false && (
        // Labels the group (below), not a single control
        <FormLabel id={labelId} htmlFor={undefined}>
          <FieldTitle
            label={label}
            source={props.source ?? source}
            resource={resource}
            isRequired={isRequired}
          />
        </FormLabel>
      )}
      <div
        role="group"
        aria-labelledby={label !== false ? labelId : undefined}
        className="flex flex-col gap-3 py-1"
      >
        {allChoices.map((choice) => {
          const value = getChoiceValue(choice);
          const choiceId = `${id}-${String(value)}`;

          return (
            <label
              key={String(value)}
              htmlFor={choiceId}
              className={cn(
                'flex items-start gap-2 text-sm',
                isReadOnly ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'
              )}
            >
              <Checkbox
                id={choiceId}
                className="mt-0.5"
                checked={values.some((existing) =>
                  isSameValue(existing, value)
                )}
                disabled={isReadOnly}
                onCheckedChange={(checked) => handleToggle(value, checked)}
              />
              <span>{getChoiceText(choice)}</span>
            </label>
          );
        })}
      </div>
      <InputHelperText helperText={helperText} />
      <FormError />
    </FormField>
  );
};

// Loose like react-admin's `CheckboxGroupInput`: ids may be numbers in the choices and strings in the value
const isSameValue = (a: unknown, b: unknown) => String(a) === String(b);
